import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePracticeScenarios } from '@/entities/practice';
import { DailyGoalBar, useStreak } from '@/entities/streak';
import { useCurrentUser } from '@/entities/user';
import { HomePromoCarousel } from '@/widgets/home-promo-carousel';
import { RecentDialoguesCarousel, useRecentDialogues } from '@/widgets/recent-dialogues';
import { TopicsSection } from '@/widgets/topics';
import { LearningLanguageSwitcher } from '@/features/learning-language-switch';
import { OnboardingReset } from '@/features/onboarding';
import { SubscriptionReset } from '@/features/paywall';
import { SessionProbe } from '@/features/session-probe';
import {
  MOCK_ERRORS_MODES,
  getMockErrorsMode,
  isMockEmpty,
  setMockEmpty,
  setMockErrorsMode,
  type MockErrorsMode,
} from '@/shared/api';
import { ErrorScreen, LoadingView, PageLayout } from '@/shared/ui';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { ROUTES, env } from '@/shared/config';

/**
 * Главный экран: пока карточка свободной практики — остальная продуктовая
 * часть (roadmap, словарь) появится в следующих спринтах.
 *
 * Вместо названия в шапке — переключатель изучаемого языка: всё ниже
 * (практика, недавние разговоры) относится к выбранному языку. Кроме
 * полосы дневной цели и стрика в самом верху — они общие на все языки.
 *
 * Ошибки: каждый блок с данными (стрик, «Готовые темы», «Предыдущие диалоги»)
 * показывает свою карточку ошибки. Упало всё разом — значит, дело не в блоке,
 * а в сети/сервере, и вместо четырёх одинаковых карточек один экран с картинкой.
 * Хуки те же, что внутри виджетов: кеш и запрос «в полёте» общие, лишних
 * запросов нет.
 */
export function HomePage() {
  const { t } = useTranslation('HomePage');
  const { user, error, isLoading, reload } = useCurrentUser();
  const navigate = useNavigate();
  // На компьютере главная — сетка плиток, как рабочий стол веб-кабинета
  // (WEB.md): цель дня — белая плитка, промо-карточки и разговоры —
  // в две колонки (раскладку держат сами виджеты по `useInDesktopShell`).
  const desktop = useInDesktopShell();
  const restartOnboarding = useCallback(() => {
    void navigate(ROUTES.onboarding, { replace: true });
  }, [navigate]);

  const streak = useStreak();
  const roleplays = usePracticeScenarios('roleplay');
  const idioms = usePracticeScenarios('idiom');
  const dialogues = useRecentDialogues();
  const blocks = [streak, roleplays, idioms, dialogues];
  const allFailed = blocks.every((block) => block.error != null);
  const reloadAll = () => {
    for (const block of blocks) block.reload();
  };

  if (error) {
    return <ErrorScreen className="min-h-dvh" onRetry={reload} />;
  }

  if (isLoading || !user) {
    return <LoadingView label={t('loadingLabel')} />;
  }

  return (
    <PageLayout title={<LearningLanguageSwitcher />}>
      {allFailed ? (
        <ErrorScreen onRetry={reloadAll} />
      ) : (
        <div className={desktop ? 'flex flex-col gap-7' : 'flex flex-col gap-9'}>
          {desktop ? (
            <div className="rounded-2xl bg-glass p-4 shadow-[0_6px_16px_rgba(0,0,0,0.06)]">
              <DailyGoalBar />
            </div>
          ) : (
            <DailyGoalBar />
          )}
          <HomePromoCarousel />
          <TopicsSection />
          <RecentDialoguesCarousel />
        </div>
      )}
      {env.isDev && (
        <div className="mt-auto flex flex-col items-center gap-1.5 pt-4">
          <SessionProbe />
          <OnboardingReset onReset={restartOnboarding} />
          <SubscriptionReset />
          <MockErrorsToggle />
          <MockEmptyToggle />
        </div>
      )}
    </PageLayout>
  );
}

/**
 * Dev: режим мока ошибок (см. `shared/api/mockErrors`): выкл / всё / A / B.
 * Перезагрузка — чтобы кеш `useCachedQuery` не показывал уже загруженные
 * данные вместо заглушек.
 */
const MOCK_LABELS: Record<MockErrorsMode, string> = { all: 'всё', a: 'A', b: 'B' };

function MockErrorsToggle() {
  const current = getMockErrorsMode();
  const options: (MockErrorsMode | null)[] = [null, ...MOCK_ERRORS_MODES];
  return (
    <div className="flex items-center gap-2 text-xs text-ink-subtle">
      <span>Мок ошибок:</span>
      {options.map((mode) => (
        <button
          key={mode ?? 'off'}
          type="button"
          onClick={() => {
            setMockErrorsMode(mode);
            window.location.reload();
          }}
          className={
            mode === current
              ? 'rounded-full bg-glass-3 px-2 py-0.5 font-semibold text-ink'
              : 'px-2 py-0.5 underline underline-offset-2'
          }
        >
          {mode ? MOCK_LABELS[mode] : 'выкл'}
        </button>
      ))}
    </div>
  );
}

/**
 * Dev: «чистый лист» (см. `shared/api/mockEmpty`) — всё по нулям, как у
 * только что пришедшего человека. Обнуляется на клиенте, база не трогается.
 */
function MockEmptyToggle() {
  const on = isMockEmpty();
  return (
    <div className="flex items-center gap-2 text-xs text-ink-subtle">
      <span>Чистый лист:</span>
      {[false, true].map((value) => (
        <button
          key={String(value)}
          type="button"
          onClick={() => {
            setMockEmpty(value);
            window.location.reload();
          }}
          className={
            value === on
              ? 'rounded-full bg-glass-3 px-2 py-0.5 font-semibold text-ink'
              : 'px-2 py-0.5 underline underline-offset-2'
          }
        >
          {value ? 'вкл' : 'выкл'}
        </button>
      ))}
    </div>
  );
}
